import { defineStore } from 'pinia'
import type { ISelectItem } from '@/components/ui/select/types'
import type { ICountry, ICountryByLanguage, ICountriesResponse } from '@/stores/types'
import {
  COUNTRIES_LIST,
  BALTIC_COUNTRY_LIST,
  isInternalLanguage,
  OTHER_COUNTRY,
  FREQUENTLY_USED_COUNTRIES,
  FREQUENTLY_USED_PHONE_COUNTRY_CODES
} from '@/utils/constants/locale'
import type { TInternalLanguage } from '@/utils/constants/locale'
import { useLocaleStore } from '@/stores/Locale.store'
import { useDynamicTranslation } from '@/composables/useDynamicTranslation'
import { sortByLocaleCompare } from '@/utils/localeCompare'

export const useCountriesStore = defineStore('countriesStore', () => {
  const localeStore = useLocaleStore()

  const { translate } = useDynamicTranslation()

  const countries = ref<ICountry>({})
  const cachedCountries = ref<ICountryByLanguage>({})

  const otherCountriesLabel = ref<string>('')

  watch(
    () => localeStore.selectedLanguage,
    async (language) => {
      if (!language) return

      if (isInternalLanguage(language)) {
        otherCountriesLabel.value = await translate('country.other')
        getCountriesFromAPI()
      }
    },
    { immediate: true }
  )

  const setCountries = (value: ICountry, language: TInternalLanguage) => {
    if (!value) return

    countries.value = value
    cachedCountries.value[language] = value
  }

  const availableCountries = computed(() => {
    let countriesData: ISelectItem[] = []

    const otherCountry = OTHER_COUNTRY(otherCountriesLabel.value)

    if (Object.keys(countries.value).length === 0) {
      return [otherCountry]
    }

    const countryData: ISelectItem[] = COUNTRIES_LIST.filter((id: string) => !!countries.value[id]).map(
      (id: string) => ({
        id: countries.value[id].code,
        label: countries.value[id].name,
        image: countries.value[id].flagPng
      })
    )
    const balticData = countryData.splice(0, BALTIC_COUNTRY_LIST.length)

    sortByLocaleCompare(countryData, 'label')

    countriesData = [...balticData, ...countryData, otherCountry]

    const isCountryAvailable = countriesData.find((item: ISelectItem) => item.id === localeStore.selectedCountry)

    if (!isCountryAvailable) {
      localeStore.setSelectedCountry(countriesData[0].id)
    }

    return countriesData
  })

  const sortedCountriesWithPopularFirst = computed<ISelectItem[]>(() => {
    if (Object.keys(countries.value).length === 0) {
      return []
    }

    const countriesData: ISelectItem[] = Object.values(countries.value)
      .filter((country) => country.code !== 'RU')
      .map((country) => ({
        id: country.code,
        label: country.name,
        image: country.flagPng
      }))

    const popularCountries: ISelectItem[] = []

    for (const countryCode of FREQUENTLY_USED_COUNTRIES) {
      const countryIndex = countriesData.findIndex((country) => country.id === countryCode)

      if (countryIndex === -1) continue

      const [country] = countriesData.splice(countryIndex, 1)
      popularCountries.push(country)
    }

    sortByLocaleCompare(countriesData, 'label')

    return [...popularCountries, ...countriesData]
  })

  const getCountriesFromAPI = async () => {
    const language = localeStore.selectedLanguage

    if (!isInternalLanguage(language)) return

    const cached = cachedCountries.value[language]

    if (cached) {
      setCountries(cached, language)

      return
    }

    try {
      const response = await $fetch<ICountriesResponse>('/api/countries', {
        query: {
          language
        }
      })

      setCountries(response._children, language)
    } catch (error) {
      console.error('Failed to fetch data:', error)
    }
  }

  const countryPhoneCodes = computed(() => {
    if (Object.keys(countries.value).length === 0) {
      return {
        countriesList: []
      }
    }

    const allCountries = Object.values(countries.value).map((country) => ({
      id: country.code,
      code: country.code,
      label: `${country.name} (${country.dialingCode})`,
      displayLabel: `${country.dialingCode}`,
      image: country.flagPng,
      name: country.name
    }))

    const frequentlyUsedCountries: typeof allCountries = []

    for (const countryCode of FREQUENTLY_USED_PHONE_COUNTRY_CODES) {
      const countryIndex = allCountries.findIndex((country) => country.code === countryCode)

      if (countryIndex === -1) continue

      const [country] = allCountries.splice(countryIndex, 1)
      frequentlyUsedCountries.push(country)
    }

    sortByLocaleCompare(allCountries, 'name')

    return {
      countriesList: [
        {
          label: 'countryCodes.frequentlyUsed',
          options: frequentlyUsedCountries
        },
        {
          label: 'countryCodes.allCountries',
          options: allCountries
        }
      ]
    }
  })

  return {
    countries,
    availableCountries,
    countryPhoneCodes,
    sortedCountriesWithPopularFirst,
    getCountriesFromAPI,
    cachedCountries
  }
})
